<template>
  <!-- Top of the third-party page, same layout as the system page hero (US 9371, US 9370) -->
  <header class="sx-hero" qid="vendor-hero">
    <button type="button"
            class="sx-hero__logo"
            :class="{ 'has-image': logo, 'is-editable': canEdit }"
            :disabled="!canEdit"
            :title="canEdit ? $t('vendor_hero.menu.logo') : ''"
            qid="vendor-hero-logo"
            @click="$emit('logo')">
      <img v-if="logo" :src="logo" alt="">
      <template v-else>{{ initials }}</template>
    </button>

    <div class="sx-hero__main">
      <router-link :to="{ name: 'contracts.third_parties' }" class="sx-hero__back" qid="vendor-back">
        <font-awesome-icon icon="angle-left"/>{{ $t('maintenance.third_parties.vendors.title') }}
      </router-link>
      <h1 class="sx-hero__name" qid="tp-view-page-title">{{ vendor.name }}</h1>
      <p v-if="vendor.description" class="sx-hero__text">{{ vendor.description | truncate(220) }}</p>
      <div class="sx-hero__tags">
        <span class="sx-hero__tag is-mint"><font-awesome-icon icon="file-alt"/>{{ $tc('vendor_hero.contracts', contractCount, { count: contractCount }) }}</span>
        <span v-if="countryNames" class="sx-hero__tag"><font-awesome-icon icon="globe"/>{{ countryNames }}</span>
        <span v-if="vendor.company_number" class="sx-hero__tag"><font-awesome-icon icon="building"/>{{ vendor.company_number }}</span>
        <span v-if="!vendor.partition && getClient.parent" class="sx-hero__tag"><font-awesome-icon icon="lock"/>{{ $t('systems_overview.from_workspace', { name: getClient.parent.name }) }}</span>
        <span v-for="tag in shownTags" :key="tag.id || tag.name_translated" class="sx-hero__tag" :title="tag.name_translated"><font-awesome-icon icon="tag"/>{{ tag.name_translated | truncate(30) }}</span>
        <span v-if="hiddenTags" class="sx-hero__tag">+{{ hiddenTags }}</span>
      </div>
    </div>

    <div class="sx-hero__people" qid="vendor-owners-list">
      <small>{{ $t('vendor_hero.people') }}</small>
      <template v-if="people.length">
        <div class="sx-hero__avatars">
          <Avatar v-for="user in people.slice(0, 4)" :key="user.id" :object="user" size="sm" :noOwner="true"></Avatar>
        </div>
        <span class="sx-hero__who">
          {{ people[0].name }}
          <span v-if="people.length > 1" class="sx-hero__faint">{{ $t('system_hero.more_people', { count: people.length - 1 }) }}</span>
        </span>
      </template>
      <span v-else class="sx-hero__who is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_hero.nobody') }}</span>
    </div>

    <div class="sx-hero__done" qid="vendor-completeness">
      <svg class="sx-hero__ring" viewBox="0 0 76 76" aria-hidden="true">
        <circle cx="38" cy="38" r="32" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="7"/>
        <circle v-if="done"
                cx="38" cy="38" r="32" fill="none" stroke="#7FE0C4" stroke-width="7" stroke-linecap="round"
                transform="rotate(-90 38 38)"
                :stroke-dasharray="ringDash"/>
      </svg>
      <div class="sx-hero__done-text is-clamped" :title="missingText">
        <b>{{ $t('system_hero.filled', { done: done, total: checks.length }) }}</b>
        <span>{{ missingText }}</span>
      </div>
    </div>

    <div class="sx-hero__act">
      <div class="sx-hero__row">
        <!-- History opens from the hero instead of the tab bar, as on projects -->
        <button type="button"
                class="sx-hero__btn"
                :class="{ 'is-white': historyOn }"
                v-b-tooltip.hover
                :title="$t('maintenance.third_parties.tab.history')"
                qid="vendor-open-history"
                @click="$emit('history')">
          <font-awesome-icon icon="history"/><span class="sr-only">{{ $t('maintenance.third_parties.tab.history') }}</span>
        </button>
        <button v-if="canEdit"
                type="button"
                class="sx-hero__btn sx-hero__grow"
                qid="edit-tp-button"
                @click="$emit('edit')">
          <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
        </button>
        <b-dropdown right
                    no-caret
                    variant="link"
                    toggle-class="sx-hero__btn"
                    menu-class="sx-menu"
                    qid="vendor-more">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span>
          </template>
          <b-dropdown-item v-if="canEdit" @click="$emit('edit')"><font-awesome-icon icon="pen"/>{{ $t('system_hero.menu.edit') }}</b-dropdown-item>
          <b-dropdown-item v-if="canEdit" qid="vendor-menu-logo" @click="$emit('logo')"><font-awesome-icon icon="cloud-upload-alt"/>{{ $t('vendor_hero.menu.logo') }}</b-dropdown-item>
          <b-dropdown-item qid="vendor-print" @click="print"><font-awesome-icon icon="file-alt"/>{{ $t('system_hero.menu.export') }}</b-dropdown-item>
          <template v-if="canDelete">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item link-class="is-danger" qid="delete-tp-button" @click="$emit('delete')"><font-awesome-icon icon="trash-alt"/>{{ $t('system_hero.menu.delete') }}</b-dropdown-item>
          </template>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'VendorHero',
    components: {
      Avatar
    },
    props: {
      vendor: { type: Object, required: true },
      // The third party's contracts as the Contracts tab loads them (first page) and how many there are
      contracts: { type: Array, default: () => [] },
      contractCount: { type: Number, default: 0 },
      // The third party's history is open
      historyOn: { type: Boolean, default: false }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      editable() {
        return !this.getClient.parent || this.vendor.partition;
      },
      canEdit() {
        return this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.vendor) && this.editable;
      },
      canDelete() {
        return this.isGranted([this.permissions.THIRD_PARTY_DELETE, this.permissions.THIRD_PARTY_DELETE_OWN], this.vendor) && this.editable;
      },
      logo() {
        return this.vendor.logo_large || this.vendor.logo || null;
      },
      initials() {
        let words = (this.vendor.name || '').trim().split(/\s+/).filter(Boolean);
        if (words.length > 1) {
          return (words[0][0] + words[1][0]).toUpperCase();
        }
        return (words[0] || '').slice(0, 3);
      },
      countryNames() {
        return this.vendor.country || '';
      },
      tags() {
        return this.vendor.tags_translated || [];
      },
      shownTags() {
        return this.tags.slice(0, 3);
      },
      hiddenTags() {
        return Math.max(this.tags.length - 3, 0);
      },
      // A third party has no owner of its own: the people who own its contracts look after it
      people() {
        let seen = {};
        let people = [];
        this.contracts.forEach(function(contract) {
          (contract.users || []).forEach(function(user) {
            if (!seen[user.id]) {
              seen[user.id] = true;
              people.push(user);
            }
          });
        });
        return people;
      },
      checks() {
        return [
          { key: 'address', ok: !!(this.vendor.address && this.vendor.city) },
          { key: 'country', ok: !!this.countryNames },
          { key: 'company_number', ok: !!this.vendor.company_number },
          { key: 'logo', ok: !!this.logo },
          { key: 'contract', ok: this.contractCount > 0 }
        ];
      },
      done() {
        return this.checks.filter(check => check.ok).length;
      },
      ringDash() {
        let length = 2 * Math.PI * 32;
        return (length * this.done / this.checks.length) + ' ' + length;
      },
      missingText() {
        let missing = this.checks.filter(check => !check.ok);
        if (!missing.length) {
          return this.$t('system_hero.all_filled');
        }
        let names = missing.map(check => this.$t('vendor_hero.missing.' + check.key).toLowerCase());
        return this.$t('system_hero.missing', { list: names.join(', ') });
      }
    },
    methods: {
      print() {
        window.print();
      }
    }
  }
</script>
